import React from 'react'
import { useMutation, useQuery } from 'react-query'
import { Navigate, useNavigate, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { InformationCircleIcon } from '@heroicons/react/solid'

import experienceService from '../services/experienceService'
import { queryClient } from '../utils/react-query'

import LoadingScreen from '../components/LoadingScreen'
import BreadCrumbs from '../components/BreadCrumbs'
import DeleteButton from '../components/DeleteButton'
import Spinner from '../components/Spinner'
import ReplyForm from '../components/Experience/ReplyForm'
import { useUser } from '../hooks/useUser'
import { BuzzlUsersError } from '../types/buzzl-user'

const Experience: React.FC = () => {
  const { experienceId } = useParams<{ experienceId: string }>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const user = useUser()

  const experience = useQuery(['experience', experienceId], () => experienceService.getById(experienceId || ''))

  const deleteExperience = useMutation(() => experienceService.deleteById(experienceId || ''), {
    onSuccess: async () => {
      queryClient.refetchQueries(['experiences'])
      toast.success('Experience has been deleted.')
      navigate('/experiences')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })
  const replyToExperience = useMutation(experienceService.replyToExperience, {
    onSuccess: async () => {
      queryClient.refetchQueries(['experience', experienceId])
      toast.success('The reply has been sent.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const handleOnReplySubmit = async (values: { reply: string }) => {
    await replyToExperience.mutateAsync({
      id: experienceId || '',
      text: values.reply,
    })
  }

  if (experience.isLoading) {
    return <LoadingScreen />
  }

  if (!experience.data) return <Navigate to="/experiences" />

  const breadCrumbPages = [
    { name: 'Experiences', href: '/experiences' },
    { name: 'Details', href: pathname },
  ]
  return (
    <div>
      <BreadCrumbs breadCrumbPages={breadCrumbPages} />
      <div className="mb-10 flex items-center justify-between">
        <h1 className="text-lg font-medium leading-6 text-gray-900">Experience</h1>
        {user.data?.type === 'BUZZL' ? (
          <DeleteButton
            type="button"
            className="btn btn-delete ml-4"
            modalTitle="Delete experience"
            modalText="This action is irreversible. Please continue
                    carefully."
            disabled={deleteExperience.isLoading}
            onClick={async () => {
              await deleteExperience.mutateAsync()
            }}
          >
            {deleteExperience.isLoading ? (
              <div className="mr-3">
                <Spinner className="trailing-icon h-4 w-4" />
              </div>
            ) : null}
            Delete Experience
          </DeleteButton>
        ) : null}
      </div>
      <div className="overflow-hidden bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex justify-between">
            <h3 className="text-lg font-medium leading-6 text-gray-900">{experience.data.title}</h3>
            {experience.data.isValidated ? (
              <span className="tag positive-tag">Verified</span>
            ) : experience.data.isVerified ? (
              <span className="tag positive-tag">At Location</span>
            ) : (
              <span className="tag alert-tag">Unverified</span>
            )}
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {new Date(experience.data.createdAt).toLocaleDateString('en-GB')}{' '}
            {new Date(experience.data.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Full name</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {experience.data.author.user.displayName}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Location</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{experience.data.place.name}</dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Customer</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {experience.data.place.customer?.name || '-'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Area</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {experience.data.place.location.city}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Rating</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {experience.data.evaluation === 'NEGATIVE' ? <span className="tag negative-tag">Negative</span> : null}
                {experience.data.evaluation === 'POSITIVE' ? <span className="tag positive-tag">Positive</span> : null}
                {experience.data.evaluation === 'NEUTRAL' ? <span className="tag alert-tag">Neutral</span> : null}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{experience.data.text}</dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Images</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {experience.data.images.length < 1 ? '-' : null}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3">
                  {experience.data.images.map((image) => (
                    <img
                      key={image.reference}
                      className="h-48 w-48 rounded object-cover"
                      src={image.url}
                      alt={image.altText}
                    />
                  ))}
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>
      {user.data?.type === 'CUSTOMER' ? (
        <div className="mt-6 mb-12 overflow-hidden bg-white px-4 py-5 shadow sm:rounded-lg sm:px-6">
          <p className="mb-6 text-lg font-medium leading-6 text-gray-900">Your reply</p>
          {experience.data.reply && experience.data.reply.text ? (
            <div>
              <textarea
                className="block h-20 w-full appearance-none rounded-md border-0 px-3 py-2 placeholder-gray-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400 sm:text-sm"
                disabled
              >
                {experience.data.reply.text}
              </textarea>
              <div className="mt-4 rounded-md bg-blue-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3 flex-1 md:flex md:justify-between">
                    <p className="text-sm text-blue-700">It is now up to the user to start a conversation with you.</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ReplyForm onSubmit={handleOnReplySubmit} />
          )}
        </div>
      ) : null}
    </div>
  )
}

export default Experience
